import { useCallback, useEffect, useRef } from 'react';

import type { MNHotInstance } from '../../tableHotInstanceTypes';
import { isHotInstanceAvailable } from '../../utils/hotInstanceUtils/hotTableInstanceValidationUtils';

export type TableContextMenuAction = (hotInstance: MNHotInstance) => void | Promise<void>;

type UseTableContextMenuActionsArgs = {
    getHotInstance: () => MNHotInstance | null;
};

/** Disables Handsontable keyboard handling while a Roma table menu owns focus. */
export const useTableContextMenuKeyboardHandling = (
    getHotInstance: () => MNHotInstance | null,
    isOpen: boolean,
): void => {
    useEffect(() => {
        if (!isOpen) return;

        const hotInstance = getHotInstance();
        if (!isHotInstanceAvailable(hotInstance)) return;

        hotInstance.unlisten();
        return () => {
            if (isHotInstanceAvailable(hotInstance) && !hotInstance.milanoteProps?.isReadOnly) hotInstance.listen();
        };
    }, [getHotInstance, isOpen]);
};

/** Shares safe deferred grid and synchronous clipboard actions across table menu entry points. */
const useTableContextMenuActions = ({ getHotInstance }: UseTableContextMenuActionsArgs) => {
    const isMountedRef = useRef(true);

    useEffect(() => () => {
        isMountedRef.current = false;
    }, []);

    const runGridAction = useCallback(
        (action: TableContextMenuAction): void => {
            const hotInstance = getHotInstance();
            if (!isHotInstanceAvailable(hotInstance)) return;

            // Capture the invoking table before the menu closes and active-table state can change.
            requestAnimationFrame(() => {
                if (!isMountedRef.current || !isHotInstanceAvailable(hotInstance)) return;
                if (hotInstance.milanoteProps?.isReadOnly) return;

                hotInstance.listen();
                try {
                    const actionResult = action(hotInstance);
                    if (actionResult) void actionResult.catch(() => undefined);
                } catch {
                    // Grid actions must not create an unhandled error at the deferred boundary.
                }
            });
        },
        [getHotInstance],
    );

    const runClipboardAction = useCallback(
        (action: TableContextMenuAction): void => {
            const hotInstance = getHotInstance();
            if (!isHotInstanceAvailable(hotInstance)) return;

            try {
                // Invoke synchronously so browser clipboard APIs retain the menu click's user activation.
                const actionResult = action(hotInstance);
                if (actionResult) void actionResult.catch(() => undefined);
            } catch {
                // Clipboard actions must not create an unhandled error from the menu event.
            }
        },
        [getHotInstance],
    );

    return { runGridAction, runClipboardAction };
};

export default useTableContextMenuActions;
